import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Header } from '../components/Header';
import { useAuthStore } from '../store/authStore';
import { useSubscriptionStore } from '../store/subscriptionStore';
import { Shield, CheckCircle, AlertCircle, CreditCard, Zap, Sun } from 'lucide-react';
import type { SubscriptionTierCode } from '../types';
import { stripeProducts } from '../stripe-config';
import { getSubscriptionStatus } from '../lib/stripe';

const isSubscriptionTierCode = (value: string | null): value is SubscriptionTierCode =>
  value === 'free' || value === 'business' || value === 'enterprise' || value === 'ultimate';

export function SubscriptionPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const { currentTier, plans, upgradeTier, setCurrentTier } = useSubscriptionStore();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Check for success or canceled query parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const successParam = queryParams.get('success');
    const canceledParam = queryParams.get('canceled');
    const tierParam = queryParams.get('tier');
    const tierCode = isSubscriptionTierCode(tierParam) ? tierParam : null;

    if (successParam === 'true' && tierCode) {
      setIsProcessing(true);
      
      // First check if the subscription is active in Stripe
      getSubscriptionStatus()
        .then(async (subscriptionData) => {
          if (subscriptionData && subscriptionData.subscription_status === 'active') {
            // Update the user's subscription tier in the database
            const result = await setCurrentTier(tierCode);
            if (result) {
              setSuccess(`Successfully upgraded to ${tierCode.charAt(0).toUpperCase() + tierCode.slice(1)} plan!`);
            } else {
              setError('Failed to update subscription status. Please contact support.');
            }
          } else {
            // If subscription is not active yet, wait a moment and try again
            setTimeout(async () => {
              const refreshedData = await getSubscriptionStatus();
              if (refreshedData && refreshedData.subscription_status === 'active') {
                const result = await setCurrentTier(tierCode);
                if (result) {
                  setSuccess(`Successfully upgraded to ${tierCode.charAt(0).toUpperCase() + tierCode.slice(1)} plan!`);
                } else {
                  setError('Failed to update subscription status. Please contact support.');
                }
              } else {
                // Force update the tier anyway since payment was successful
                const result = await setCurrentTier(tierCode);
                if (result) {
                  setSuccess(`Successfully upgraded to ${tierCode.charAt(0).toUpperCase() + tierCode.slice(1)} plan!`);
                } else {
                  setError('Failed to update subscription status. Please contact support.');
                }
              }
            }, 3000);
          }
        })
        .catch((err) => {
          console.error('Error checking subscription status:', err);
          // Force update the tier anyway since payment was successful
          setCurrentTier(tierCode)
            .then((result) => {
              if (result) {
                setSuccess(`Successfully upgraded to ${tierCode.charAt(0).toUpperCase() + tierCode.slice(1)} plan!`);
              } else {
                setError('Failed to update subscription status. Please contact support.');
              }
            });
        })
        .finally(() => {
          setIsProcessing(false);
          // Remove query parameters
          navigate('/subscription', { replace: true });
        });
    } else if (canceledParam === 'true') {
      setError('Subscription upgrade was canceled.');
      // Remove query parameters
      navigate('/subscription', { replace: true });
    }
  }, [location.search, navigate, setCurrentTier]);

  // Check current subscription status on mount
  useEffect(() => {
    if (user) {
      getSubscriptionStatus()
        .then((subscriptionData) => {
          if (subscriptionData && subscriptionData.subscription_status === 'active') {
            // Determine tier from price_id
            const tierEntry = Object.entries(stripeProducts).find(
              ([, product]) => product.priceId === subscriptionData.price_id
            );
            
            if (tierEntry && tierEntry[0] !== currentTier) {
              const [tier] = tierEntry;
              setCurrentTier(tier as SubscriptionTierCode);
            }
          }
        })
        .catch((err) => {
          console.error('Error checking subscription status:', err);
        });
    }
  }, [user, currentTier, setCurrentTier]);

  if (!user) {
    navigate('/');
    return null;
  }

  const currentPlan = currentTier ? plans[currentTier] : null;

  const handleSelectTier = async (tier: SubscriptionTierCode) => {
    // If it's the current tier, do nothing
    if (tier === currentTier) return;
    
    // Clear previous messages
    setError(null);
    setSuccess(null);
    
    try {
      // For free tier, downgrade without payment
      if (tier === 'free') {
        const result = await setCurrentTier(tier);
        if (result) {
          setSuccess('Successfully downgraded to Free plan!');
        } else {
          setError('Failed to downgrade subscription. Please try again.');
        }
        return;
      }
      
      // For paid tiers, redirect to Stripe checkout
      await upgradeTier(tier);
    } catch (err) {
      setError('An error occurred during the upgrade process.');
      console.error(err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header 
        profile={user}
        onLogout={() => {
          useAuthStore.getState().signOut();
          navigate('/');
        }}
        onSettings={() => navigate('/settings')}
        onDashboard={() => navigate('/dashboard')}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Subscription Management</h1>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center text-red-800">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                {error}
              </div>
            </div>
          )}

          {success && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center text-green-800">
                <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                {success}
              </div>
            </div>
          )}

          {isProcessing && (
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center text-blue-800">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                Processing your subscription upgrade...
              </div>
            </div>
          )}

          {currentPlan && (
            <div className="bg-blue-50 rounded-xl p-6 mb-8">
              <div className="flex items-start">
                <Shield className="h-6 w-6 text-blue-600 mt-1 mr-3" />
                <div>
                  <h2 className="text-xl font-bold text-blue-900 mb-2">
                    Current Plan: {currentTier === 'free' ? 'Free' : 
                                  currentTier === 'business' ? 'Business' : 
                                  currentTier === 'enterprise' ? 'Enterprise' : 'Ultimate'}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="flex items-center mb-2">
                        <Zap className="h-5 w-5 text-blue-500 mr-2" />
                        <span className="font-medium">Projects</span>
                      </div>
                      <p className="text-2xl font-bold">{currentPlan.maxProjects === 999999 ? "Unlimited" : currentPlan.maxProjects}</p>
                      <p className="text-sm text-gray-600">Maximum projects</p>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="flex items-center mb-2">
                        <Sun className="h-5 w-5 text-amber-500 mr-2" />
                        <span className="font-medium">System Size</span>
                      </div>
                      <p className="text-2xl font-bold">{currentPlan.systemSizeLimit}kW</p>
                      <p className="text-sm text-gray-600">Maximum system size</p>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="flex items-center mb-2">
                        <CreditCard className="h-5 w-5 text-green-500 mr-2" />
                        <span className="font-medium">Price</span>
                      </div>
                      <p className="text-2xl font-bold">SAR {currentPlan.priceMonthly}/mo</p>
                      <p className="text-sm text-gray-600">Monthly subscription</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Available Plans
            </h2>
            <p className="text-gray-600 mb-6">
              Choose the plan that best fits your needs
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {Object.entries(plans).map(([tier, plan]) => (
                <div
                  key={tier}
                  className={`bg-white rounded-xl p-6 shadow-lg border-2 transition-all flex flex-col h-full ${
                    currentTier === tier
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300 cursor-pointer'
                  }`}
                  onClick={() => !isProcessing && currentTier !== tier && handleSelectTier(tier as SubscriptionTierCode)}
                >
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900">
                      {tier === 'free' ? 'Free' : 
                       tier === 'business' ? 'Business' : 
                       tier === 'enterprise' ? 'Enterprise' : 'Ultimate'}
                    </h3>
                    <Shield className="h-6 w-6 text-blue-600" />
                  </div>

                  <div className="mb-6">
                    <p className="text-3xl font-bold text-gray-900">
                      SAR {plan.priceMonthly}
                      <span className="text-base font-normal text-gray-600">/mo</span>
                    </p>
                  </div>

                  <ul className="space-y-3 mb-6 flex-grow">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-gray-600">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isProcessing && currentTier !== tier) {
                        handleSelectTier(tier as SubscriptionTierCode);
                      }
                    }}
                    disabled={currentTier === tier || isProcessing}
                    className={`w-full py-3 px-4 rounded-lg font-medium transition-all ${
                      currentTier === tier
                        ? 'bg-gray-100 text-gray-600 cursor-not-allowed'
                        : isProcessing
                        ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {currentTier === tier
                      ? 'Current Plan'
                      : isProcessing
                      ? 'Processing...'
                      : tier === 'free'
                      ? 'Downgrade to Free'
                      : `Upgrade to ${tier === 'business' ? 'Business' : tier === 'enterprise' ? 'Enterprise' : 'Ultimate'}`}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Plan Comparison</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Feature</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Free</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enterprise</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ultimate</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Maximum Projects</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">1</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">5</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">10</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Unlimited</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">System Size Limit</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">20kW</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">50kW</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">250kW</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">2000kW</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Support Level</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Basic</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Priority</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Dedicated</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">White Glove</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Custom Reporting</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="text-red-500">✕</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="text-green-500">✓</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="text-green-500">✓</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="text-green-500">✓</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Team Collaboration</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="text-red-500">✕</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="text-green-500">✓</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="text-green-500">✓</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="text-green-500">✓</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Custom Branding</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="text-red-500">✕</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="text-red-500">✕</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="text-green-500">✓</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="text-green-500">✓</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Custom Integrations</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="text-red-500">✕</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="text-red-500">✕</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="text-red-500">✕</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="text-green-500">✓</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Monthly Price</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">SAR 0</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">SAR 750</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">SAR 1,500</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">SAR 2,500</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
